import { apiUtils } from '../hooks';
import { Enterprise } from '../hooks/useEnterprises';
import { throwApiError } from '../utils';
import { API_ENDPOINTS } from './apiEndpoints';
interface UseEnterprisesParams {
  search?: string;
  page?: number;
  limit?: number;
  primarySortKey?: string;
  primarySortOrder?: 'asc' | 'desc' | null;
}

interface SearchEnterpriseResponse {
  id: string;
  name: string;
}

export interface Quarter {
  id: string;
  fiscal_year: number;
  fiscal_quarter: number;
  quarter_label: string;
  start_date: string;
  end_date: string;
  is_active: boolean;
  is_locked: boolean;
  can_upload: boolean;
  created_at: string;
  updated_at: string;
}

interface QuartersResponse {
  data: Quarter[];
}

interface SyncHotelsResponse {
  success: boolean;
  message: string;
  _timezone: {
    requested: string;
    applied: boolean;
  };
}

export interface LinkedEnterprise {
  id: string;
  name: string;
  financial_quarter: string | null;
}
interface LinkedEnterprisesResponse {
  success: boolean;
  message: string;
  data: LinkedEnterprise[];
}
class EnterpriseService {
  async getUserLinkedEnterprises() {
    try {
      const response = await apiUtils.get<LinkedEnterprisesResponse>(API_ENDPOINTS.enterpriseAdmin.linkedEnterprises);
      return response.data;
    } catch (error) {
      throwApiError(error, "Failed to get Linked Enterprises");
      return null
    }
  }
  async getEnterprises(queryParams: UseEnterprisesParams): Promise<any> {
    // Simulate an API call
    try {
      const response = await apiUtils.get<any>(
        API_ENDPOINTS.enterprises.list,
        queryParams
      );
      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch enterprises');
      return [];
    }
  }

  async getEnterpriseById(id: string): Promise<Enterprise> {
    try {
      const response = await apiUtils.get<Enterprise>(
        `${API_ENDPOINTS.enterprises.detail(id)}`
      );
      return response.data.data;
    } catch (error) {
      throwApiError(error, `Failed to fetch enterprise with id ${id}`);
      throw error; // This line will never be reached due to throwApiError
    }
  }

  async createEnterprise(
    data: Partial<Enterprise>,
    is_test?: boolean
  ): Promise<Enterprise> {
    try {
      const response = await apiUtils.post<Enterprise>(
        API_ENDPOINTS.enterprises.create,
        { ...data, ...(is_test !== undefined ? { is_test } : {}) }
      );
      return response.data.data;
    } catch (error) {
      throwApiError(error, 'Failed to create enterprise');
      return {} as Enterprise;
    }
  }

  async updateEnterprise(
    id: string,
    data: Partial<Enterprise>
  ): Promise<Enterprise> {
    try {
      const response = await apiUtils.put<Enterprise>(
        `${API_ENDPOINTS.enterprises.update(id)}`,
        data
      );
      return response.data.data;
    } catch (error) {
      throwApiError(error, `Failed to update enterprise with id ${id}`);
      return {} as Enterprise;
    }
  }

  async deleteEnterprise(id: string): Promise<void> {
    try {
      await apiUtils.delete<void>(`${API_ENDPOINTS.enterprises.delete(id)}`);
    } catch (error) {
      throwApiError(error, `Failed to delete enterprise with id ${id}`);
    }
  }
  async users(enterpriseId: string, searchValue?: string): Promise<any> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.enterprises.users(enterpriseId, searchValue ?? '')
      );
      return response.data;
    } catch (error) {
      throwApiError(
        error,
        `Failed to fetch users for enterprise with id ${enterpriseId}`
      );
      return [];
    }
  }

  async getAssignedHotels(
    enterpriseId: string,
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    sortKey: string | null = null,
    sortOrder: 'asc' | 'desc' | null = null
  ): Promise<any> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.enterprises.assignedHotels(enterpriseId),
        {
          page,
          limit,
          search: searchTerm,
          primarySortKey: sortKey,
          primarySortOrder: sortOrder,
        }
      );

      return response?.data;
    } catch (error) {
      throwApiError(
        error,
        `Failed to fetch assigned hotels for enterprise with id ${enterpriseId}`
      );
      return 0;
    }
  }

  async getUnassignedHotels(
    enterpriseId: string,
    page: number = 1,
    limit: number = 10,
    searchTerm: string = '',
    hotelSortKey: string | null = null,
    hotelSortOrder: 'asc' | 'desc' | null = null
  ): Promise<any> {
    try {
      const response = await apiUtils.get<{ hotels_count: number }>(
        API_ENDPOINTS.enterprises.unassignedHotels(enterpriseId),
        {
          page,
          limit,
          search: searchTerm,
          primarySortKey: hotelSortKey || undefined,
          primarySortOrder: hotelSortOrder || undefined,
        }
      );

      return response?.data;
    } catch (error) {
      throwApiError(
        error,
        `Failed to fetch unassigned hotels for enterprise with id ${enterpriseId}`
      );
      return 0;
    }
  }
  async deleteUserFromEnterprise(enterpriseId: string, userId: string) {
    try {
      const response = await apiUtils.delete(
        API_ENDPOINTS.enterprises.removeUser(enterpriseId),
        { data: { user_ids: [userId] } }
      );
      return response;
    } catch (error) {
      throwApiError(error, 'Failed to delete user from enterprise');
    }
  }
  async getUnlinkedUsers(
    enterpriseId: string,
    searchValue: string
  ): Promise<any> {
    try {
      const response = await apiUtils.get(
        API_ENDPOINTS.enterprises.searchUsers(enterpriseId, searchValue)
      );
      return response.data;
    } catch (error) {
      throwApiError(
        error,
        `Failed to fetch users for enterprise with id ${enterpriseId}`
      );
      return 0;
    }
  }
  async linkUserToEnterprise(
    enterpriseId: string,
    userId: string
  ): Promise<any> {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.enterprises.linkUser(enterpriseId),
        { user_id: userId }
      );
      return response.data;
    } catch (error) {
      throwApiError(
        error,
        `Failed to link users to enterprise with id ${enterpriseId}`
      );
      return 0;
    }
  }
  async onAssignHotels(enterpriseId: string, hotelIds: string[]) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.enterprises.assign(enterpriseId),
        {
          hotel_ids: hotelIds,
        }
      );

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to add hotels to another enterprise');
    }
  }
  async onUnAssignHotels(enterpriseId: string, hotelIds: string[]) {
    try {
      const response = await apiUtils.post(
        API_ENDPOINTS.enterprises.remove(enterpriseId),
        {
          hotel_ids: hotelIds,
        }
      );

      return response.data;
    } catch (error) {
      throwApiError(error, 'Failed to remove hotels from enterprise');
    }
  }

  async searchEnterprises(
    searchTerm: string
  ): Promise<SearchEnterpriseResponse[]> {
    try {
      const response = await apiUtils.get<SearchEnterpriseResponse[]>(
        API_ENDPOINTS.enterprises.search(searchTerm)
      );
      return response.data.data;
    } catch (error) {
      throwApiError(error, 'Failed to search enterprises');
      return [];
    }
  }

  async syncHotelsFromEnterprise(
    enterpriseId: string,
    fromEnterpriseId: string,
    replace_all: boolean,
    quarterId?: string
  ): Promise<SyncHotelsResponse> {
    try {
      const response = await apiUtils.post<SyncHotelsResponse>(
        API_ENDPOINTS.enterprises.syncHotelsFromEnterprise(enterpriseId),
        {
          from_enterprise_id: fromEnterpriseId,
          quarter_id: quarterId,
          replace_all: replace_all,
        }
      );
      return (response as any).data;
    } catch (error) {
      throwApiError(error, 'Failed to sync hotels from enterprise');
      throw error;
    }
  }

  async syncHotelsFromFiles(
    enterpriseId: string,
    formData: FormData
  ): Promise<SyncHotelsResponse> {
    try {
      const response = await apiUtils.post<SyncHotelsResponse>(
        API_ENDPOINTS.enterprises.syncHotelsFromFiles(enterpriseId),
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return (response as any).data;
    } catch (error) {
      throwApiError(error, 'Failed to sync hotels from files');
      throw error;
    }
  }

  async getQuarters(): Promise<Quarter[]> {
    try {
      const response = await apiUtils.get<QuartersResponse>(
        API_ENDPOINTS.enterprises.quarters
      );
      return (response as any).data.data;
    } catch (error) {
      throwApiError(error, 'Failed to fetch quarters');
      return [];
    }
  }
}
export const enterpriseService = new EnterpriseService();
